/**
 * tauriPrint.js — Tauri-safe print popup wrapper
 *
 * PROBLEM:
 *   Tauri v2 WebView mein window.open('', '_blank') block ho sakta hai ya
 *   unexpected behaviour deta hai (OS-level browser open karne ki koshish karta hai).
 *   App mein 7 jagah popup print use hoti hai — ye sab is ek utility se fix ho jaayegi.
 *
 * SOLUTION:
 *   isTauri() detect karta hai runtime pe. Agar Tauri mein hai:
 *     → HTML content ko ek hidden <iframe> mein inject karo, print karo, cleanup karo.
 *   Agar browser/web mein hai:
 *     → Original window.open popup as-is (no change to browser behaviour).
 *
 * USAGE (drop-in replacement):
 *
 *   // PEHLE (old code):
 *   const w = window.open('', '_blank', 'width=900,height=700');
 *   w.document.write(htmlString);
 *   w.document.close();
 *   setTimeout(() => w.print(), 300);
 *
 *   // BAAD (new code — sirf ek line change):
 *   import { tauriSafePrint } from '@/src/utils/tauriPrint.js';
 *   tauriSafePrint(htmlString);
 *
 * AFFECTED FILES (ye sab files update karo — find/replace):
 *   1. src/ui/printing.js         — printVoucher(), printInvoice() (lines ~200, ~284)
 *   2. src/modules/hr/payroll.js  — line ~291
 *   3. src/modules/orders/orders.js — line ~239
 *   4. src/modules/accounting/vouchers.js — line ~671
 *   5. src/modules/accounting/registers.js — line ~75 (inline onclick)
 *   6. src/modules/reports/keyboard_reports.js — line ~66
 *   7. src/components/PrintManager.tsx — popupPrint() function (line ~87)
 */

/**
 * Tauri environment detect karta hai.
 * __TAURI_INTERNALS__ Tauri v2 mein injected hota hai.
 * Tauri v1 ke liye: window.__TAURI__ check karo.
 */
export function isTauri() {
  return (
    typeof window !== 'undefined' &&
    (
      typeof window.__TAURI_INTERNALS__ !== 'undefined' ||
      typeof window.__TAURI__ !== 'undefined'
    )
  );
}

/**
 * Main function — Tauri aur browser dono mein print karta hai.
 *
 * @param {string} htmlString - Complete HTML string (DOCTYPE se </html> tak)
 * @param {object} [options]
 * @param {number} [options.printDelay=400] - Print dialog open hone ka delay (ms)
 * @param {string} [options.iframeId='__tauri_print_frame__'] - Hidden iframe ka ID
 */
export function tauriSafePrint(htmlString, options = {}) {
  const {
    printDelay = 400,
    iframeId = '__tauri_print_frame__',
  } = options;

  if (isTauri()) {
    // ── TAURI MODE: iframe-based print ──────────────────────────────────────
    _iframePrint(htmlString, { printDelay, iframeId });
  } else {
    // ── BROWSER MODE: original popup window (unchanged behaviour) ───────────
    _popupPrint(htmlString);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal: Iframe print (Tauri ke liye)
// ─────────────────────────────────────────────────────────────────────────────
function _iframePrint(htmlString, { printDelay, iframeId }) {
  // Purani frame saaf karo agar reh gayi ho
  const existing = document.getElementById(iframeId);
  if (existing) existing.remove();

  const iframe = document.createElement('iframe');
  iframe.id = iframeId;
  iframe.style.cssText = [
    'position:fixed',
    'top:-10000px',
    'left:-10000px',
    'width:1px',
    'height:1px',
    'border:none',
    'opacity:0',
    'pointer-events:none',
  ].join(';');

  document.body.appendChild(iframe);

  const iDoc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!iDoc) {
    console.error('[tauriPrint] iframe document access failed — fallback window.print()');
    window.print();
    return;
  }

  iDoc.open();
  iDoc.write(htmlString);
  iDoc.close();

  // Fonts + resources load hone ka wait karo, phir print
  const win = iframe.contentWindow;
  if (!win) {
    console.error('[tauriPrint] iframe contentWindow null');
    return;
  }

  // document.fonts.ready agar available ho
  const fontsReady = iDoc.fonts
    ? iDoc.fonts.ready
    : Promise.resolve();

  fontsReady.then(() => {
    setTimeout(() => {
      try {
        win.focus();
        win.print();
      } catch (e) {
        console.error('[tauriPrint] print() failed:', e);
        window.print(); // last resort fallback
      }

      // Print hone ke baad cleanup
      const cleanup = () => {
        setTimeout(() => {
          const frame = document.getElementById(iframeId);
          if (frame) frame.remove();
        }, 1500);
      };

      if (win.onafterprint !== undefined) {
        win.onafterprint = cleanup;
      } else {
        // Safari / some WebViews mein onafterprint nahi hota
        setTimeout(cleanup, 3000);
      }
    }, printDelay);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal: Popup print (Browser ke liye — original behaviour)
// ─────────────────────────────────────────────────────────────────────────────
function _popupPrint(htmlString) {
  const popup = window.open('', '_blank', 'width=900,height=700,scrollbars=yes');
  if (popup) {
    popup.document.open();
    popup.document.write(htmlString);
    popup.document.close();
    popup.document.fonts?.ready?.then?.(() => {
      setTimeout(() => {
        popup.focus();
        popup.print();
        popup.onafterprint = () => popup.close();
      }, 300);
    }) ?? setTimeout(() => {
      popup.focus();
      popup.print();
      popup.onafterprint = () => popup.close();
    }, 400);
  } else {
    // Popup blocked fallback
    console.warn('[tauriPrint] Popup blocked — using window.print()');
    if (typeof showToast === 'function') {
      showToast('Popup blocked! Please allow popups for printing.', 'warning');
    }
    window.print();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Convenience: registers.js ke inline onclick ke liye (global attach)
// registers.js mein button ke onclick mein call karo: window.__tauriPrint(html)
// ─────────────────────────────────────────────────────────────────────────────
if (typeof window !== 'undefined') {
  window.__tauriPrint = tauriSafePrint;
}
